/* @flow */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import {getLocalizedURL} from '@intl/adobe-locales';
import Banner from '@react/react-spectrum/Banner';
import Apps from '@react/react-spectrum/Icon/Apps';
import CCLibrary from '@react/react-spectrum/Icon/CCLibrary';
import Download from '@react/react-spectrum/Icon/Download';
import Education from '@react/react-spectrum/Icon/Education';
import {DeviceDetectionUtil} from 'cchome-shared/utils';
import {History} from 'history';
import React, {Component} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {getEnvironment} from '../../../utils/Environment';
import open from '../../../utils/open';
import * as USERTYPES from '../../containers/Home/constants';
import ScrollCarousel from '../ScrollCarousel/ScrollCarousel';
import SingleAppQuickLinksAnalytics from './Analytics/SingleAppQuickLinksAnalytics';
import * as QUICK_LINKS_DATA from './QuickLinksData';
import './SingleAppQuickLinks.scss';

interface SingleAppQuickLinksProps extends RouteComponentProps {
	entitlement: string | void;
	isAcrobatDC: boolean;
	history: History;
	intl: any;
	language: string;
	planCode: string;
	fetchQuickLinks: Function;
	quickLinks: Array<QuickLinksArrayContent>;
	fetchQuickLinksComplete: boolean;
	fetchQuickLinksError: boolean;
}

interface State {
	opacity: number;
	shouldDisplayBanner: boolean;
	singleAppLinks: QuickLinks;
	acrobatLinks: QuickLinks;
	libraryLinks: QuickLinks;
	commLinks: QuickLinks;
}

export class SingleAppQuickLinks extends Component<SingleAppQuickLinksProps, State> {
	singleAppLinks: any = QUICK_LINKS_DATA.SINGLE_APP_QUICK_LINKS;
	acrobatLinks: any = QUICK_LINKS_DATA.ACROBAT_DC_APP_QUICK_LINKS;
	libraryLinks: any = QUICK_LINKS_DATA.LIBRARIES_QUICK_LINKS;
	commLinks: any = QUICK_LINKS_DATA.COMM_QUICK_LINKS;

	state: State = {
		opacity: 0,
		shouldDisplayBanner: true,
		singleAppLinks: {} as QuickLinks,
		acrobatLinks: {} as QuickLinks,
		libraryLinks: {} as QuickLinks,
		commLinks: {} as QuickLinks,
	};

	quickLinksToDisplay: any = {};
	isUCSFeatureEnabled: boolean = window.cchome.util.hasFeature('ucs-quicklinks');

	componentDidMount() {
		setTimeout(() => {
			this.setState({opacity: 1});
		}, 500);

		window.addEventListener('resize', this.onBreakpointChanged);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onBreakpointChanged);
	}

	onBreakpointChanged = () => {
		const span = document.querySelector('.your-apps-home-quick-link-cards-hypertext-span.with-banner');
		const banner = document.querySelector('.your-apps-home-quick-link-cards-hypertext .spectrum-Banner');
		if (span && banner) {
			if (banner.getBoundingClientRect().left - 12 <= span.getBoundingClientRect().right) {
				this.setState({shouldDisplayBanner: false});
			}
			else {
				this.setState({shouldDisplayBanner: true});
			}
		}
	};

	isURL(url: string): Array<string> | void | null {
		return url.match(
			/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g,
		);
	}

	postCardRender() {
		const analytics = new SingleAppQuickLinksAnalytics();
		this.quickLinksToDisplay.cards.forEach((card) => {
			analytics.postCardAction('render', card.name, card.link);
		});
	}

	postCardsAction(card: any) {
		const analytics = new SingleAppQuickLinksAnalytics();
		let link = card.link;
		const env = getEnvironment();

		if (card.name === 'fonts') {
			if (env !== 'prod') {
				link = 'https://fonts-relstage.adobe.com';
			}
		}
		else if (card.name === 'stock') {
			if (link === 'https://stock.adobe.com') {
				// there are two stock cards, one default one for comm users
				if (env !== 'prod') {
					link = 'https://primary.stock.stage.adobe.com/';
				}
				link
					+= '/promo/firstmonthfree?as_channel=adobe_apps&as_campclass=brand&as_camptype=app&as_source=CCHome&as_content=Stock-card&as_campaign=apps';
			}
		}
		else if (card.name === 'cclibrary' && this.props.entitlement === 'Communicator') {
			if (env !== 'prod') {
				link = card.linkStage;
			}
		}

		analytics.postCardAction('click', card.name, link);
		this.handleOnClick(link);
	}

	handleOnClick = (someLink = '') => {
		let link = someLink;
		const target = link === '/quick-actions/pattern' ? '_self' : '_blank';
		if (this.isURL(link) || link === '/quick-actions/pattern') {
			if (link === '/quick-actions/pattern') {
				link = window.location.origin + link;
			}
			open(getLocalizedURL(link, this.props.language), target);
		}
		else {
			this.props.history.push(link);
		}
	};

	renderIcon(icon: string, iconColor: string) {
		switch (icon) {
			case 'Pattern':
				return (
					<svg
						xmlns='http://www.w3.org/2000/svg'
						width='32'
						height='32'
						viewBox='0 0 32 32'
						className='pattern-icon'
					>
						<path
							id='Path_101801'
							data-name='Path 101801'
							d='M29.394,26.435a6.933,6.933,0,0,1,1.914,3.336,1.326,1.326,0,0,1-1.289,1.6,1.35,1.35,0,0,1-.28-.03A6.935,6.935,0,0,1,26.4,29.428a6.857,6.857,0,0,1-1.89-3.3L16.5,18.115,8.438,26.176a6.857,6.857,0,0,1-1.873,3.252,6.92,6.92,0,0,1-3.339,1.915,1.247,1.247,0,0,1-.28.032,1.324,1.324,0,0,1-1.291-1.6,6.929,6.929,0,0,1,1.915-3.34,6.857,6.857,0,0,1,3.252-1.874L14.883,16.5,6.822,8.439A6.857,6.857,0,0,1,3.57,6.565,6.921,6.921,0,0,1,1.657,3.229a1.323,1.323,0,0,1,1.57-1.574A6.929,6.929,0,0,1,6.565,3.572,6.857,6.857,0,0,1,8.438,6.824L16.5,14.885,24.515,6.87a6.857,6.857,0,0,1,1.89-3.3,6.92,6.92,0,0,1,3.34-1.915,1.323,1.323,0,0,1,1.562,1.586,6.93,6.93,0,0,1-1.912,3.322,6.907,6.907,0,0,1-3.2,1.864L18.115,16.5l8.076,8.076A6.907,6.907,0,0,1,29.394,26.435ZM2.024,8.881H1.262A.762.762,0,0,0,.5,9.643V23.357a.762.762,0,0,0,.762.762h.762a.762.762,0,0,0,.762-.762V9.643A.762.762,0,0,0,2.024,8.881ZM9.643,32.5H23.357a.762.762,0,0,0,.762-.762v-.762a.762.762,0,0,0-.762-.762H9.643a.762.762,0,0,0-.762.762v.762A.762.762,0,0,0,9.643,32.5ZM30.214,9.643V23.357a.762.762,0,0,0,.762.762h.762a.762.762,0,0,0,.762-.762V9.643a.762.762,0,0,0-.762-.762h-.762A.762.762,0,0,0,30.214,9.643ZM23.357.5H9.643a.762.762,0,0,0-.762.762v.762a.762.762,0,0,0,.762.762H23.357a.762.762,0,0,0,.762-.762V1.262A.762.762,0,0,0,23.357.5ZM16.5,5.466a2.5,2.5,0,0,0-2.415,2.59,6.36,6.36,0,0,0,1.493,3.776l.914,1.236.914-1.236a6.344,6.344,0,0,0,1.5-3.779,2.494,2.494,0,0,0-2.4-2.587Zm0,14.464-.914,1.236a6.36,6.36,0,0,0-1.493,3.779,2.42,2.42,0,1,0,4.827,0,6.344,6.344,0,0,0-1.5-3.776Zm8.442-5.844a6.223,6.223,0,0,0-3.776,1.493l-1.236.914,1.236.914a6.6,6.6,0,0,0,3.622,1.5h.152a2.42,2.42,0,1,0,0-4.827Zm-16.887,0a2.42,2.42,0,1,0,0,4.827h.152a6.6,6.6,0,0,0,3.627-1.5L13.07,16.5l-1.236-.914a6.324,6.324,0,0,0-3.779-1.5Z'
							transform='translate(-0.5 -0.5)'
							fill='#16878c'
						/>
					</svg>
				);
			case 'Spark':
				return (
					<img
						className='spark-img'
						src='https://stg-ffc-static-cdn.oobesaas.adobe.com/icons/FFC-SPRK/1.0/48x48.png'
						alt='Spark'
					/>
				);
			case 'Rush':
				return (
					<img
						className='rush-img'
						src='https://stg-ffc-static-cdn.oobesaas.adobe.com/icons/RUSH/1.5.50/48x48.png'
						alt='Premiere Rush'
					/>
				);
			case 'CCLibrary':
				return <CCLibrary className={`color-${iconColor}`} size='XL' />;
			case 'Discover':
				return (
					<svg
						xmlns='http://www.w3.org/2000/svg'
						width='32'
						height='32'
						viewBox='0 0 32 32'
						className='discover-icon'
					>
						<g id='icn_quickaction' transform='translate(-0.076 0.35)'>
							<g id='Group_22' data-name='Group 22' transform='translate(2.298 1.619)'>
								<path
									id='Path_112'
									data-name='Path 112'
									className='cls-1'
									d='M9.8,3.164l2.3,7.312,6.591,2.569L12.1,15.615,9.8,22.928,7.483,15.615.9,13.045l6.583-2.569Z'
									transform='translate(-0.9 -0.013)'
								/>
								<path
									id='Path_113'
									data-name='Path 113'
									className='cls-1'
									d='M16.858,1,18.39,5.388l4.4,1.541-4.4,1.532-1.532,4.4-1.542-4.4L10.929,6.929l4.386-1.541Z'
									transform='translate(3.676 -1)'
								/>
								<path
									id='Path_114'
									data-name='Path 114'
									className='cls-1'
									d='M16.943,11.864l1.532,4.388,4.4,1.541-4.4,1.542-1.532,4.388L15.4,19.335l-4.386-1.542L15.4,16.252Z'
									transform='translate(3.715 3.957)'
								/>
							</g>
							<rect
								id='Rectangle_56'
								data-name='Rectangle 56'
								className='cls-2'
								width='32'
								height='32'
								transform='translate(0.076 -0.35)'
							/>
						</g>
					</svg>
				);
			case 'Fonts':
				return (
					<svg
						id='fonts_icon'
						xmlns='http://www.w3.org/2000/svg'
						width='32'
						height='32'
						viewBox='0 0 32 32'
						className='fonts-icon'
					>
						<rect id='Canvas' className='cls-1' width='32' height='32' />
						<path
							id='Path_82793'
							data-name='Path 82793'
							className='cls-2'
							d='M12.667,10a.667.667,0,0,1,.667.667v4a.667.667,0,0,1-.667.667H11.333a.667.667,0,0,1-.667-.667v-2H8V22h2a.667.667,0,0,1,.667.667V24a.667.667,0,0,1-.667.667H3.333A.667.667,0,0,1,2.667,24V22.667A.667.667,0,0,1,3.333,22h2V12.667H2.667v1.981A.667.667,0,0,1,2,15.315H.667A.667.667,0,0,1,0,14.648V10.667A.667.667,0,0,1,.667,10Z'
							transform='translate(0 3.333)'
						/>
						<path
							id='Path_82794'
							data-name='Path 82794'
							className='cls-2'
							d='M28.333,3h-20A1.333,1.333,0,0,0,7,4.333V9a.667.667,0,0,0,.667.667H9A.667.667,0,0,0,9.667,9V5.667h6.667V24.333h-2a.667.667,0,0,0-.667.667v1.333a.667.667,0,0,0,.667.667h8A.667.667,0,0,0,23,26.333V25a.667.667,0,0,0-.667-.667h-2V5.667H27V9a.667.667,0,0,0,.667.667H29A.667.667,0,0,0,29.667,9V4.333A1.333,1.333,0,0,0,28.333,3Z'
							transform='translate(2.333 1)'
						/>
					</svg>
				);
			case 'Stock':
				return (
					<svg
						xmlns='http://www.w3.org/2000/svg'
						width='32'
						height='30.545'
						viewBox='0 0 32 32'
						className='stock-icon'
					>
						<g id='S_Stock_24_N_2x' data-name='S_Stock_24_N@2x' transform='translate(-2 -2)'>
							<path
								id='Path_79143'
								data-name='Path 79143'
								className='cls-1'
								d='M14.088,26.484a.3.3,0,0,1-.123-.278V24.114c0-.062.075-.124.169-.062a5.531,5.531,0,0,0,3.182.973c1.568,0,2.213-.823,2.213-1.75,0-.823-.4-1.5-1.906-2.384l-.788-.415c-2.213-1.254-2.858-2.539-2.858-4.165,0-2.214,1.521-3.995,4.411-3.995a5.764,5.764,0,0,1,2.739.509c.108.031.123.109.123.232v1.951a.1.1,0,0,1-.169.093,4.962,4.962,0,0,0-2.643-.651c-1.66,0-2.2.945-2.2,1.7,0,.836.415,1.44,1.89,2.26l.692.374c2.459,1.363,3.074,2.7,3.074,4.32,0,2.4-1.737,4.072-4.58,4.072a6.654,6.654,0,0,1-3.227-.7Z'
								transform='translate(-3.344 -3.008)'
							/>
							<path
								id='Path_79144'
								data-name='Path 79144'
								className='cls-1'
								d='M32.16,27.168a.232.232,0,0,1-.153.232,4.432,4.432,0,0,1-1.6.264c-1.629,0-2.566-.867-2.566-2.911V18.839H26.626c-.108,0-.138-.046-.138-.155V16.949c0-.109.031-.17.153-.17h1.229a24.206,24.206,0,0,1,.184-2.508.209.209,0,0,1,.153-.217l1.937-.248c.092-.031.153,0,.153.109-.046.619-.061,1.687-.075,2.864h1.888c.108,0,.153.031.153.139V18.7c0,.109-.075.139-.153.139H30.192v5.5c0,.9.308,1.3,1.106,1.3a2.944,2.944,0,0,0,.722-.075c.061-.016.138.016.138.124Z'
								transform='translate(-6.79 -3.416)'
							/>
							<path
								id='Path_79145'
								data-name='Path 79145'
								className='cls-2'
								d='M2,3.455V31.091a1.455,1.455,0,0,0,1.455,1.455H32.545A1.455,1.455,0,0,0,34,31.091V3.455A1.455,1.455,0,0,0,32.545,2H3.455A1.455,1.455,0,0,0,2,3.455ZM31.091,29.636H4.909V4.909H31.091Z'
								transform='translate(0)'
							/>
						</g>
					</svg>
				);
			case 'CreativeCloud':
				return (
					<svg
						id='Layer_2'
						data-name='Layer 2'
						xmlns='http://www.w3.org/2000/svg'
						viewBox='0 0 32 32'
						className='cc-icon'
					>
						<path
							id='_44x42'
							data-name='44x42'
							d='M43.01317,11.45814A18.33236,18.33236,0,0,0,29.58449,5.99929,17.00963,17.00963,0,0,0,17.1248,11.23442,15.30732,15.30732,0,0,0,.09437,26.45112,15.7685,15.7685,0,0,0,4.38608,37.13905,14.98372,14.98372,0,0,0,15.39376,41.9003h14.5881a18.04753,18.04753,0,0,0,17.9568-18.09683A18.34672,18.34672,0,0,0,43.01317,11.45814ZM15.12139,39.10807A12.28805,12.28805,0,0,1,2.796,26.537c0-6.97347,5.23274-12.7454,12.18984-12.7454a12.88068,12.88068,0,0,1,8.507,3.329l.06212.05369,5.25657,5.39394a2.26068,2.26068,0,0,1-3.245,3.1484l-5.21606-5.35281a8.10117,8.10117,0,0,0-5.2828-1.95918,7.86373,7.86373,0,0,0-7.79438,8.0997A8.06965,8.06965,0,0,0,15.2436,34.681l1.04117.00476.00966.02932c1.49069,2.05666,5.64317,4.393,5.64317,4.393Zm25.99116-4.38192a15.2739,15.2739,0,0,1-11.03924,4.46907,15.10781,15.10781,0,0,1-10.73008-4.59073l-6.94746-6.96224a2.26513,2.26513,0,0,1,1.62066-3.84868,2.24008,2.24008,0,0,1,1.62046.68162l6.89867,6.91787a11.051,11.051,0,0,0,7.712,3.37939,10.46553,10.46553,0,0,0,7.63716-3.22669,10.8803,10.8803,0,0,0,2.79882-7.81932A10.54743,10.54743,0,0,0,30.2082,12.8804a9.709,9.709,0,0,0-6.00866,1.752c-.01992-.01618-.51883-.3469-1.08229-.68923-.34947-.21249-.72584-.42383-1.022-.58452-.3295-.17916-1.27411-.64259-1.83609-.87487-.13985-.05788-.32041-.12414-.39648-.151l.0158-.00857c2.8595-2.673,6.21091-3.575,10.12266-3.575A14.80954,14.80954,0,0,1,45.14992,23.66108,15.48936,15.48936,0,0,1,41.11255,34.72615Z'
						/>
					</svg>
				);
			case 'Education':
				return <Education className={`color-${iconColor}`} />;
			case 'Download':
				return <Download className={`color-${iconColor}`} />;
			case 'Apps':
				return <Apps className={`color-${iconColor}`} />;
			case 'CreatePDF':
				return (
					<div
						className='png-icon'
						style={{backgroundImage: 'url(\'./assets/singleappquicklinks/create-pdf.svg\')'}}
					/>
				);
			case 'CombinePDF':
				return (
					<div
						className='png-icon'
						style={{backgroundImage: 'url(\'./assets/singleappquicklinks/combine-pdf.svg\')'}}
					/>
				);
			case 'ExportPDF':
				return (
					<div
						className='png-icon'
						style={{backgroundImage: 'url(\'./assets/singleappquicklinks/convert-pdf.svg\')'}}
					/>
				);
		}
	}

	renderQuickLinks() {
		this.postCardRender();

		const width = window.cchome.util.hasFeature('apphome_new_carousel') ? '308px' : '100%';

		const cards = (
			<>
				{this.quickLinksToDisplay.cards.map((card, i) => (
					<div
						className='your-apps-home-quick-link-cards'
						style={{width}}
						key={`${card.title}-${i}`}
						tabIndex={0}
					>
						<div className='your-apps-home-quick-link-cards-text'>
							<h3 className='your-apps-home-quick-link-cards-title'>
								{!this.isUCSFeatureEnabled && this.props.intl.formatMessage({id: card.title})}
								{this.isUCSFeatureEnabled && <span>{card.title}</span>}
							</h3>
							<div className='your-apps-home-quick-link-cards-icon-description'>
								<div className='quick-link-cards-svg-container'>
									{this.renderIcon(card.icon, card.iconColor)}
								</div>
								{!this.isUCSFeatureEnabled && <FormattedMessage id={card.desc} />}
								{this.isUCSFeatureEnabled && <span className='card-description'>{card.desc}</span>}
							</div>
						</div>
						<div
							className={`your-apps-home-quick-link-cards-hypertext ${
								!this.state.shouldDisplayBanner ? 'hide-banner' : ''
							}`}
						>
							<span
								className={`your-apps-home-quick-link-cards-hypertext-span ${
									card.isNew ? ' with-banner' : ''
								}`}
								onClick={() => this.postCardsAction(card)}
							>
								{!this.isUCSFeatureEnabled && <FormattedMessage id={card.hypertext} />}
								{this.isUCSFeatureEnabled && <span>{card.hypertext}</span>}
							</span>
							{card.isNew && <Banner content={this.props.intl.formatMessage({id: 'app.new'})} />}
						</div>
					</div>
				))}
			</>
		);

		return <ScrollCarousel scrollDistance={324}>{cards}</ScrollCarousel>;
	}

	render() {
		if (window.cchome.util.hasFeature('hide_recommended_for_you') && !this.props.isAcrobatDC) {
			return null;
		}
		let singleAppQuickLinks = {} as QuickLinks;
		let acrobatDcAppQuickLinks = {} as QuickLinks;
		let librariesQuickLinks = {} as QuickLinks;
		let communicatorQuickLinks = {} as QuickLinks;

		// Wait till the response is recieved and the state is updated
		if (this.isUCSFeatureEnabled) {
			if (!this.props.fetchQuickLinksComplete || this.props.fetchQuickLinksError || !this.props.quickLinks[0]) {return null;}

			singleAppQuickLinks = this.props.quickLinks[0].singleAppQuickLinks;
			acrobatDcAppQuickLinks = this.props.quickLinks[0].acrobatDcAppQuickLinks;
			librariesQuickLinks = this.props.quickLinks[0].librariesQuickLinks;
			communicatorQuickLinks = this.props.quickLinks[0].communicatorQuickLinks;
		}

		if (this.props.entitlement === 'CCE_Free') {
			return null;
		}
		else if (this.props.entitlement === 'Communicator') {
			this.quickLinksToDisplay = this.isUCSFeatureEnabled ? communicatorQuickLinks : this.commLinks;
		}
		else if (
			window.cchome.util.hasFeature('Library_QuickLinks')
			&& this.props.language === 'en'
			&& !DeviceDetectionUtil.isInternetExplorer()
			&& !this.props.isAcrobatDC
			&& this.props.planCode
			&& this.props.planCode.indexOf('enterprise') === -1
		) {
			this.quickLinksToDisplay = this.isUCSFeatureEnabled ? librariesQuickLinks : this.libraryLinks;

			// Manipulate the constant response based on ff to display the quicklinks
			if (
				!this.isUCSFeatureEnabled
				&& window.cchome.util.hasFeature('new_quickLink_card')
				&& window.cchome.util.hasFeature('QuickLinks_Pattern')
			) {
				this.quickLinksToDisplay.cards[0].isNew = false;
				this.quickLinksToDisplay.cards[1] = {
					icon: 'Pattern',
					title: 'app.yourApps.quickLinks.pattern.title',
					desc: 'app.yourApps.quickLinks.pattern.desc',
					link: '/quick-actions/pattern',
					hypertext: 'app.yourApps.quickLinks.pattern.hypertext',
					name: 'pattern',
					iconColor: '',
					isNew: true,
				};
			}
		}
		else {
			if (this.props.entitlement !== USERTYPES.SINGLE_APP_USER_TYPE) {
				return null;
			}

			if (this.props.isAcrobatDC) {
				this.quickLinksToDisplay = this.isUCSFeatureEnabled ? acrobatDcAppQuickLinks : this.acrobatLinks;
			}
			else {
				this.quickLinksToDisplay = this.isUCSFeatureEnabled ? singleAppQuickLinks : this.singleAppLinks;
			}
		}
		const inlineStyle = {opacity: this.state.opacity};

		return (
			<div className='your-apps-home-quick-link-container home-component' style={inlineStyle}>
				<h3 className='your-apps-home-quick-link-title'>
					{this.isUCSFeatureEnabled && <span>{this.quickLinksToDisplay.title}</span>}
					{!this.isUCSFeatureEnabled && <FormattedMessage id={this.quickLinksToDisplay.title} />}
				</h3>
				<div className='your-apps-home-quick-link-cards-container'>{this.renderQuickLinks()}</div>
			</div>
		);
	}
}

export default withRouter(injectIntl<SingleAppQuickLinksProps>(SingleAppQuickLinks));
